/**
 * Окно карточки вилки при столе AdsPower: одно на все вилки, из Edge.
 *
 * Клик его не двигает и не перезагружает: `window.open('', имя)` у живого окна
 * адрес не трогает (с `about:blank` окно ушло бы в пустой документ уже после
 * отрисовки — белое окно). Место — где человек его оставил; нет памяти — колонка
 * справа на мониторе, где открывали столы.
 */

import { type Desk, deskOf, forkColumn, paneFeatures } from './deskPanes';

export const DESK_PANEL = 'forq-fork-desk';
const PANEL_KEY = 'forq.next.desk.panel';
const AREA_KEY = 'forq.next.desk.area';

let panel: Window | null = null;

function alive(win: Window | null): win is Window {
  if (win === null) return false;
  try {
    return win.closed !== true;
  } catch {
    return false;
  }
}

function readBox(key: string): Desk | null {
  try {
    const raw = JSON.parse(localStorage.getItem(key) ?? 'null') as Partial<Desk> | null;
    if (raw === null) return null;
    const { availLeft, availTop, availWidth, availHeight } = raw;
    if (![availLeft, availTop, availWidth, availHeight].every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
    if ((availWidth as number) < 200 || (availHeight as number) < 200) return null;
    return raw as Desk;
  } catch {
    return null;
  }
}

function writeBox(key: string, box: Desk): void {
  try {
    localStorage.setItem(key, JSON.stringify(box));
  } catch {
    //
  }
}

/** Монитор, где открывали столы: там стоят профили, там и карточка. */
export function rememberDeskArea(area: Desk): void {
  writeBox(AREA_KEY, area);
}

export function deskArea(): Desk | null {
  return readBox(AREA_KEY);
}

function homeBox(): Desk | null {
  const saved = readBox(PANEL_KEY);
  if (saved !== null) return saved;
  const area = deskArea() ?? deskOf(window.screen);
  return forkColumn(area);
}

/** Окно карточки: живое — как есть, без движения; нет — рождается на своём месте. */
export function deskPanel(): Window | null {
  if (alive(panel)) return panel;
  const box = homeBox();
  let win: Window | null = null;
  try {
    win = box === null ? window.open('', DESK_PANEL) : window.open('', DESK_PANEL, paneFeatures(box));
  } catch {
    win = null;
  }
  if (!alive(win)) return null;
  panel = win;
  return win;
}

/**
 * Карточку — рядом с окнами касс (место считает расширение по окнам профилей и
 * мониторам). Уже стоит там — не трогаем: окно не дёргается от клика к клику.
 */
export function placeDeskPanel(box: { left: number; top: number; width: number; height: number }): void {
  if (!alive(panel)) return;
  const win = panel;
  try {
    const off = Math.abs(win.screenX - box.left) > 12 || Math.abs(win.screenY - box.top) > 12
      || Math.abs(win.outerWidth - box.width) > 24 || Math.abs(win.outerHeight - box.height) > 24;
    if (off) {
      win.moveTo(Math.round(box.left), Math.round(box.top));
      win.resizeTo(Math.round(box.width), Math.round(box.height));
    }
  } catch {
    // Окно не двигается — останется, где стоит.
  }
  writeBox(PANEL_KEY, { availLeft: box.left, availTop: box.top, availWidth: box.width, availHeight: box.height });
}

/** «Открыть столы»: окно карточки сразу, с подписью «тут будет вилка». */
export function openDeskPanel(): void {
  const win = deskPanel();
  if (win === null) return;
  void import('./pop').then(({ placeHolder }) => placeHolder(win)).catch(() => undefined);
}

export function resetDeskPanel(): void {
  panel = null;
}
